import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../domain/api';

// Hook to get all ads activities with pagination and filters
export const useAdsActivities = (filters = {}) => {
  // Create a stable query key to prevent unnecessary re-fetches
  const queryKey = [
    'ads-activities',
    filters.page || 1,
    filters.limit || 10,
    filters.status || '',
    filters.platform || '',
    filters.assigned_to || '',
    filters.client || '',
  ];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.status) params.append('status', filters.status);
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.assigned_to)
        params.append('assigned_to', filters.assigned_to);
      if (filters.client) params.append('client', filters.client);

      const response = await api.get(`/ads-activities?${params.toString()}`);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    keepPreviousData: true, // Keep previous data while fetching new data
    refetchOnWindowFocus: false, // Prevent refetch on window focus
  });

  return [
    {
      activities: query.data?.adsActivities || [],
      pagination: query.data?.pagination || {},
      loading: query.isLoading,
      error: query.error,
    },
    query.refetch,
  ];
};

// Hook to get a single ads activity by ID
export const useSingleAdsActivity = (id) => {
  const query = useQuery({
    queryKey: ['ads-activity', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await api.get(`/ads-activities/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  return [
    {
      activity: query.data,
      loading: query.isLoading,
      error: query.error,
    },
    query.refetch,
  ];
};

// Hook to get ads activities dashboard stats
export const useAdsActivityStats = () => {
  const query = useQuery({
    queryKey: ['ads-activities-stats'],
    queryFn: async () => {
      const response = await api.get('/ads-activities/stats');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return [
    {
      stats: query.data?.stats || {},
      recentActivities: query.data?.recentActivities || [],
      loading: query.isLoading,
      error: query.error,
    },
    query.refetch,
  ];
};

// Hook to create a new ads activity
export const useCreateAdsActivity = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/ads-activities', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ads-activities']);
      queryClient.invalidateQueries(['ads-activities-stats']);
    },
  });

  const createActivity = (data) => mutation.mutateAsync(data);

  return [
    createActivity,
    {
      loading: mutation.isLoading,
      error: mutation.error,
    },
  ];
};

// Hook to update campaign status within an ads activity
export const useUpdateCampaignStatus = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ activityId, campaignId, data }) => {
      const response = await api.put(
        `/ads-activities/${activityId}/campaigns/${campaignId}`,
        data
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['ads-activity', variables.activityId]);
      queryClient.invalidateQueries(['ads-activities']);
      queryClient.invalidateQueries(['ads-activities-stats']);
    },
  });

  const updateCampaignStatus = (activityId, campaignId, data) =>
    mutation.mutateAsync({ activityId, campaignId, data });

  return [
    updateCampaignStatus,
    {
      loading: mutation.isLoading,
      error: mutation.error,
    },
  ];
};

// Hook to add update/comment to an ads activity
export const useAddAdsActivityUpdate = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, comment }) => {
      const response = await api.post(`/ads-activities/${id}/updates`, {
        comment,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['ads-activity', variables.id]);
      queryClient.invalidateQueries(['ads-activities']);
    },
  });

  const addUpdate = (id, comment) => mutation.mutateAsync({ id, comment });

  return [
    addUpdate,
    {
      loading: mutation.isLoading,
      error: mutation.error,
    },
  ];
};

// Hook to update ads activity status
export const useUpdateAdsActivityStatus = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const response = await api.put(`/ads-activities/${id}/status`, {
        status,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['ads-activity', variables.id]);
      queryClient.invalidateQueries(['ads-activities']);
      queryClient.invalidateQueries(['ads-activities-stats']);
    },
  });

  const updateStatus = (id, status) => mutation.mutateAsync({ id, status });

  return [
    updateStatus,
    {
      loading: mutation.isLoading,
      error: mutation.error,
    },
  ];
};

// Hook to delete an ads activity
export const useDeleteAdsActivity = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/ads-activities/${id}`);
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ads-activities']);
      queryClient.invalidateQueries(['ads-activities-stats']);
    },
  });

  const deleteActivity = (id) => mutation.mutateAsync(id);

  return [
    deleteActivity,
    {
      loading: mutation.isLoading,
      error: mutation.error,
    },
  ];
};
